import React, { useCallback, useContext, useEffect } from "react";
import { Box } from "@mui/material";
import EcoScanContext from "./contexts/EcoScan.context";
import useEcoScanState from "./hooks/useEcoScanState.hooks";
import { useIntl } from "react-intl";
import OverviewContainer from "./components/Overview/OverviewContainer";
import CommunityAnalysisContainer from "./components/CommunityAnalysis/CommunityAnalysisContainer";
import WebsiteTrafficContainer from "./components/WebsiteTraffic/WebsiteTrafficContainer";
import DAppTrackingContainer from "./components/DAppTracking/DAppTrackingContainer";
import { sortArrayByKey } from "features/helpers/utils";

const EcoScanContainer = ({ type }) => {
  const { formatMessage } = useIntl();
  const {
    dateRange,
    setDateRange,
    selectedParty,
    setSelectedParty,
    projectPartyData,
    projectPartyDataLoading,
    overviewFilterValue,
    setOverviewFilterValue,
    morphTableData,
    setMorphTableData,
    morphTableDataLoading,
    fetchMorphTableData,
    order,
    setOrder,
    orderBy,
    setOrderBy,
  } = useEcoScanState();

  const value = {
    dateRange,
    setDateRange,
    selectedParty,
    setSelectedParty,
    projectPartyData,
    projectPartyDataLoading,
    overviewFilterValue,
    setOverviewFilterValue,
    morphTableData,
    setMorphTableData,
    morphTableDataLoading,
    order,
    setOrder,
    orderBy,
    setOrderBy,
  };

  const titleText = useCallback(() => {
    if (type === "overview") {
      return formatMessage({ id: "global.function.overview" });
    }
    if (type === "communityAnalysis") {
      return formatMessage({ id: "global.function.communityAnalysis" });
    }

    if (type === "websiteTraffic") {
      return formatMessage({ id: "global.function.websiteTraffic" });
    }

    if (type === "dAppTracking") {
      return formatMessage({ id: "global.function.dAppTracking" });
    }
  }, [type]);

  const titleDescription = useCallback(() => {
    if (type === "overview") {
      return formatMessage({ id: "ecoScan.overview.description" });
    }
    if (type === "communityAnalysis") {
      return formatMessage({ id: "ecoScan.communityAnalysis.description" });
    }

    if (type === "websiteTraffic") {
      return formatMessage({ id: "ecoScan.websiteTraffic.description" });
    }

    if (type === "dAppTracking") {
      return formatMessage({ id: "ecoScan.dAppTracking.description" });
    }
  }, [type]);

  useEffect(() => {
    const startDate = dateRange[0];
    const endDate = dateRange[1];
    fetchMorphTableData({
      from: startDate,
      to: endDate,
      type,
    });
  }, [dateRange]);

  useEffect(() => {
    if (morphTableData.length == 0) return;
    let newOrderBy = "transactionCount";

    if (type === "communityAnalysis") {
      newOrderBy = "newDiscordMember";
    }
    if (type === "websiteTraffic") {
      newOrderBy = "websiteTrafficWalletCount";
    }

    const newOrder = "desc";
    const newArr = sortArrayByKey(morphTableData, newOrderBy, newOrder);
    setOrder(newOrder);
    setOrderBy(newOrderBy);
    setMorphTableData(newArr);
  }, [type]);

  const renderContent = () => {
    if (type === "overview") {
      return <OverviewContainer type={type}></OverviewContainer>;
    }
    if (type === "communityAnalysis") {
      return <CommunityAnalysisContainer></CommunityAnalysisContainer>;
    }
    if (type === "websiteTraffic") {
      return <WebsiteTrafficContainer></WebsiteTrafficContainer>;
    }
    if (type === "dAppTracking") {
      return <DAppTrackingContainer></DAppTrackingContainer>;
    }
  };

  return (
    <EcoScanContext.Provider value={value}>
      <Box key={type}>
        <div className="fade-up">
          <Box className="title" mb={1}>
            {titleText()}
          </Box>
          <Box color={(theme) => theme.customColors.grey[600]} mb={1}>
            {titleDescription()}
          </Box>
          <Box className="startRow" sx={{ color: "#A5A5A5" }} mb={3}>
            <i className="meta-crm-icon-ic_info font-size-14 mgr4" />
            {formatMessage({ id: "global.tooltip.timeZone" })}
          </Box>
        </div>
        <div className="fade-up-more">{renderContent()}</div>
      </Box>
    </EcoScanContext.Provider>
  );
};

export default EcoScanContainer;
